import React, { useState, useEffect, useRef } from 'react';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import ConfirmDialog from './ConfirmDialog';
import { apiClient } from '../utils/apiClient';

const TwoFactorAuth = ({ onSuccess, onCancel, mode = 'setup' }) => {
  const [step, setStep] = useState(mode === 'setup' ? 'choice' : 'login');
  const [totpSecret, setTotpSecret] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [detectedMethod, setDetectedMethod] = useState(null);
  const [methodLoading, setMethodLoading] = useState(true);
  const [availableMethods, setAvailableMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [showMethodSelection, setShowMethodSelection] = useState(false);
  const [additionalMethodPrompt, setAdditionalMethodPrompt] = useState(null); // { nextStep, completedMethod }
  const totpSetupInputRef = useRef(null);
  const totpLoginInputRef = useRef(null);

  // Detect mobile on mount and on resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Detect 2FA method from server when in login mode
  useEffect(() => {
    if (mode === 'login') {
      detect2FAMethod();
    } else {
      setMethodLoading(false);
    }
  }, [mode]);

  // Autofocus the TOTP code field as soon as it's actually on screen,
  // so the user (or a password manager) can type/fill immediately.
  useEffect(() => {
    const method = selectedMethod || detectedMethod;
    if (mode === 'login' && !methodLoading && !showMethodSelection && method === 'totp') {
      totpLoginInputRef.current?.focus();
    }
  }, [mode, methodLoading, showMethodSelection, selectedMethod, detectedMethod]);

  useEffect(() => {
    if (mode === 'setup' && step === 'totp' && qrCode) {
      totpSetupInputRef.current?.focus();
    }
  }, [mode, step, qrCode]);

  const detect2FAMethod = async () => {
    try {
      const data = await apiClient.get('/api/auth/2fa/status');

      const methods = [];
      if (data.hasTotpEnabled) {
        methods.push('totp');
      }
      if (data.hasWebauthnEnabled) {
        methods.push('webauthn');
      }

      setAvailableMethods(methods);
      
      if (methods.length === 1) {
        // Single method - use directly
        setDetectedMethod(methods[0]);
        localStorage.setItem('2faEnabled', methods[0]);
      } else if (methods.length > 1) {
        // Multiple methods - show selection
        setDetectedMethod('multiple');
        setShowMethodSelection(true);
        localStorage.setItem('2faEnabled', 'multiple');
      } else {
        setDetectedMethod(null);
      }
    } catch (err) {
      console.error('Error detecting 2FA method:', err);
      // Fallback to localStorage
      const localMethod = localStorage.getItem('2faEnabled');
      setDetectedMethod(localMethod);
    } finally {
      setMethodLoading(false);
    }
  };

  // TOTP Setup Flow
  const handleTOTPSetup = async () => {
    setLoading(true);
    setError('');
    
    try {
      const data = await apiClient.post('/api/auth/2fa/totp/setup');

      setTotpSecret(data.secret);
      setQrCode(data.qrCode);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // TOTP Setup Verification (includes secret for initial setup)
  const handleTOTPSetupVerification = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await apiClient.post('/api/auth/2fa/totp/setup/verify', {
        code: verificationCode,
        secret: totpSecret
      });

      if (data.verified) {
        localStorage.setItem('2faEnabled', 'totp');

        // Ask if they want to set up a backup method
        setAdditionalMethodPrompt({ nextStep: 'webauthn', completedMethod: 'totp' });
      } else {
        setError('Invalid verification code');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // TOTP Login Verification (no secret needed - server looks it up)
  const handleTOTPLoginVerification = async (codeToVerify = null) => {
    const codeToUse = codeToVerify || verificationCode;
    if (codeToUse.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await apiClient.post('/api/auth/2fa/totp/verify', { code: codeToUse });

      if (data.verified) {
        onSuccess('totp');
      } else {
        setError('Invalid verification code');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // WebAuthn Setup
  const handleWebAuthnSetup = async () => {
    setLoading(true);
    setError('');
    
    try {
      const options = await apiClient.post('/api/auth/2fa/webauthn/register/begin');
      const attResp = await startRegistration(options);

      const verification = await apiClient.post('/api/auth/2fa/webauthn/register/finish', attResp);

      if (verification.verified) {
        localStorage.setItem('2faEnabled', 'webauthn');

        // Ask if they want to set up a backup method
        setAdditionalMethodPrompt({ nextStep: 'totp', completedMethod: 'webauthn' });
      } else {
        throw new Error('Registration verification failed');
      }
    } catch (err) {
      setError(err.message || 'WebAuthn setup failed');
    } finally {
      setLoading(false);
    }
  };

  // WebAuthn Login
  const handleWebAuthnLogin = async () => {
    setLoading(true);
    setError('');
    
    try {
      const options = await apiClient.post('/api/auth/2fa/webauthn/authenticate/begin');
      const authResp = await startAuthentication(options);

      const verification = await apiClient.post('/api/auth/2fa/webauthn/authenticate/finish', authResp);

      if (verification.verified) {
        onSuccess('webauthn');
      } else {
        throw new Error('Authentication verification failed');
      }
    } catch (err) {
      setError(err.message || 'WebAuthn authentication failed');
    } finally {
      setLoading(false);
    }
  };

  // Render Setup Choice
  const renderSetupChoice = () => (
    <div>
      <h2>Setup Two-Factor Authentication</h2>
      <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
        For maximum security, you can set up multiple authentication methods. Choose which methods you'd like to configure:
      </p>
      <div style={{ marginBottom: '20px' }}>
        <button 
          onClick={() => setStep('totp')}
          style={{ 
            width: '100%', 
            padding: '15px', 
            marginBottom: '10px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          📱 Setup Authenticator App (TOTP)
        </button>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '15px' }}>
          Use apps like Google Authenticator, Authy, or Microsoft Authenticator
        </p>
        
        <button 
          onClick={() => setStep('webauthn')}
          style={{ 
            width: '100%', 
            padding: '15px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🔑 Setup Security Key / Biometric
        </button>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '15px' }}>
          Use fingerprint, Face ID, or hardware security keys
        </p>
        
        <div style={{ 
          backgroundColor: '#e9ecef', 
          padding: '12px', 
          borderRadius: '4px', 
          fontSize: '12px', 
          color: '#495057' 
        }}>
          💡 <strong>Tip:</strong> Setting up multiple methods provides backup options if one method becomes unavailable.
        </div>
      </div>
    </div>
  );

  // Render TOTP Setup
  const renderTOTPSetup = () => (
    <div>
      <h2>Setup Authenticator App</h2>
      {qrCode ? (
        <div>
          <p>Scan this QR code with your authenticator app:</p>
          <div style={{ textAlign: 'center', margin: '20px 0' }}>
            <img src={qrCode} alt="TOTP QR Code" style={{ maxWidth: '200px' }} />
          </div>
          <p style={{ fontSize: '12px', color: '#666' }}>
            Manual entry key: <code>{totpSecret}</code>
          </p>
          <input
            ref={totpSetupInputRef}
            type="text"
            id="totp-setup-code"
            name="otp"
            autoComplete="one-time-code"
            inputMode="numeric"
            placeholder="Enter 6-digit code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            style={{ width: '100%', padding: '10px', marginBottom: '10px' }}
          />
          <button 
            onClick={handleTOTPSetupVerification}
            disabled={loading || verificationCode.length !== 6}
            style={{ 
              width: '100%', 
              padding: '10px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {loading ? 'Verifying...' : 'Verify and Enable'}
          </button>
        </div>
      ) : (
        <button 
          onClick={handleTOTPSetup}
          disabled={loading}
          style={{ 
            width: '100%', 
            padding: '10px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {loading ? 'Setting up...' : 'Generate QR Code'}
        </button>
      )}
    </div>
  );

  // Render WebAuthn Setup
  const renderWebAuthnSetup = () => (
    <div>
      <h2>Setup Security Key / Biometric</h2>
      <p>Click the button below and follow your browser's prompts to register your security method.</p>
      <button 
        onClick={handleWebAuthnSetup}
        disabled={loading}
        style={{ 
          width: '100%', 
          padding: '15px',
          backgroundColor: '#28a745',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        {loading ? 'Setting up...' : 'Setup Security Key/Biometric'}
      </button>
    </div>
  );

  // Render method selection for multiple methods
  const renderMethodSelection = () => (
    <div>
      <h2 style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: '15px' }}>
        Choose Verification Method
      </h2>
      <p style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: '20px', color: '#666' }}>
        You have multiple 2FA methods available. Choose how you'd like to verify:
      </p>
      
      {availableMethods.includes('totp') && (
        <button 
          onClick={() => {
            setSelectedMethod('totp');
            setShowMethodSelection(false);
          }}
          style={{ 
            width: '100%', 
            padding: isMobile ? '14px' : '15px',
            marginBottom: '10px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: isMobile ? '16px' : '16px',
            minHeight: isMobile ? '48px' : 'auto'
          }}
        >
          📱 Authenticator App (TOTP)
        </button>
      )}
      
      {availableMethods.includes('webauthn') && (
        <button 
          onClick={() => {
            setSelectedMethod('webauthn');
            setShowMethodSelection(false);
          }}
          style={{ 
            width: '100%', 
            padding: isMobile ? '14px' : '15px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: isMobile ? '16px' : '16px',
            minHeight: isMobile ? '48px' : 'auto'
          }}
        >
          🔑 Security Key / Biometric
        </button>
      )}
    </div>
  );

  // Render Login Verification
  const renderLoginVerification = () => {
    if (methodLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #e3e3e3',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ fontSize: isMobile ? '14px' : '16px', color: '#666' }}>
            Detecting your 2FA method...
          </p>
        </div>
      );
    }

    // Show method selection if multiple methods are available
    if (showMethodSelection) {
      return renderMethodSelection();
    }

    const method = selectedMethod || detectedMethod;

    if (method === 'totp') {
      return (
        <div>
          <h2 style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: '15px' }}>
            Enter Authentication Code
          </h2>
          <p style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: '20px' }}>
            Enter the 6-digit code from your authenticator app:
          </p>
          <input
            ref={totpLoginInputRef}
            type="text"
            id="totp-code"
            name="otp"
            autoComplete="one-time-code"
            inputMode="numeric"
            placeholder="000000"
            value={verificationCode}
            onChange={(e) => {
              const newCode = e.target.value.replace(/\D/g, '').slice(0, 6);
              setVerificationCode(newCode);
              // Auto-submit when 6 digits are entered
              if (newCode.length === 6 && !loading) {
                setTimeout(() => handleTOTPLoginVerification(newCode), 100);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && verificationCode.length === 6 && !loading) {
                handleTOTPLoginVerification();
              }
            }}
            style={{
              width: '100%', 
              padding: isMobile ? '12px' : '15px', 
              marginBottom: '15px',
              textAlign: 'center',
              fontSize: isMobile ? '20px' : '24px',
              letterSpacing: isMobile ? '6px' : '8px',
              border: '2px solid #ddd',
              borderRadius: '6px',
              boxSizing: 'border-box'
            }}
          />
          <button 
            onClick={handleTOTPLoginVerification}
            disabled={loading || verificationCode.length !== 6}
            style={{ 
              width: '100%', 
              padding: isMobile ? '14px' : '15px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: isMobile ? '16px' : '16px',
              minHeight: isMobile ? '48px' : 'auto'
            }}
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
          {availableMethods.length > 1 && (
            <button 
              onClick={() => {
                setSelectedMethod(null);
                setShowMethodSelection(true);
                setError('');
                setVerificationCode('');
              }}
              style={{ 
                width: '100%', 
                padding: isMobile ? '12px' : '10px',
                marginTop: '10px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: isMobile ? '14px' : '14px',
                minHeight: isMobile ? '44px' : 'auto'
              }}
            >
              Try Different Method
            </button>
          )}
        </div>
      );
    } else if (method === 'webauthn') {
      return (
        <div>
          <h2 style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: '15px' }}>
            Security Key / Biometric Verification
          </h2>
          <p style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: '20px' }}>
            Use your security key or biometric authentication to continue:
          </p>
          <button 
            onClick={handleWebAuthnLogin}
            disabled={loading}
            style={{ 
              width: '100%', 
              padding: isMobile ? '14px' : '15px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: isMobile ? '16px' : '16px',
              minHeight: isMobile ? '48px' : 'auto'
            }}
          >
            {loading ? 'Authenticating...' : 'Authenticate'}
          </button>
          {availableMethods.length > 1 && (
            <button 
              onClick={() => {
                setSelectedMethod(null);
                setShowMethodSelection(true);
                setError('');
              }}
              style={{ 
                width: '100%', 
                padding: isMobile ? '12px' : '10px',
                marginTop: '10px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: isMobile ? '14px' : '14px',
                minHeight: isMobile ? '44px' : 'auto'
              }}
            >
              Try Different Method
            </button>
          )}
        </div>
      );
    }
    
    return (
      <div>
        <h2 style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: '15px' }}>
          Two-Factor Authentication Required
        </h2>
        <p style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: '20px', color: '#666' }}>
          Unable to determine your 2FA method. Please try logging out and logging back in.
        </p>
        <button
          onClick={() => {
            // Clear potentially corrupted 2FA data and redirect to login
            localStorage.removeItem('2faEnabled');
            localStorage.removeItem('2faCompleted');
            localStorage.removeItem('2faSessionTime');
            localStorage.removeItem('2faElevatedTime');
            window.location.href = '/login';
          }}
          style={{ 
            width: '100%', 
            padding: isMobile ? '14px' : '15px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: isMobile ? '16px' : '16px',
            minHeight: isMobile ? '48px' : 'auto'
          }}
        >
          Reset & Return to Login
        </button>
      </div>
    );
  };

  const handleBack = () => {
    if (mode === 'setup' && step !== 'choice') {
      setStep('choice');
      setError('');
      setVerificationCode('');
      setQrCode('');
      setTotpSecret('');
    } else {
      onCancel();
    }
  };

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: 'rgba(0,0,0,0.5)', 
      display: 'flex', 
      alignItems: isMobile ? 'flex-start' : 'center', 
      justifyContent: 'center',
      zIndex: 9999,
      padding: isMobile ? '10px' : '20px',
      paddingTop: isMobile ? '20px' : '20px'
    }}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div style={{ 
        backgroundColor: 'white', 
        padding: isMobile ? '20px' : '30px', 
        borderRadius: '8px', 
        maxWidth: isMobile ? '100%' : '400px', 
        width: isMobile ? '100%' : '90%',
        maxHeight: isMobile ? '90vh' : '80vh',
        overflow: 'auto',
        margin: isMobile ? '0' : 'auto'
      }}>
        {error && (
          <div style={{ 
            color: 'red', 
            backgroundColor: '#ffe6e6', 
            padding: '10px', 
            borderRadius: '4px', 
            marginBottom: '20px',
            fontSize: isMobile ? '14px' : '16px'
          }}>
            {error}
          </div>
        )}
        
        {mode === 'setup' && step === 'choice' && renderSetupChoice()}
        {mode === 'setup' && step === 'totp' && renderTOTPSetup()}
        {mode === 'setup' && step === 'webauthn' && renderWebAuthnSetup()}
        {mode === 'login' && renderLoginVerification()}
        
        <div style={{ 
          marginTop: '20px', 
          textAlign: 'center',
          paddingBottom: isMobile ? '10px' : '0'
        }}>
          <button 
            onClick={handleBack}
            style={{ 
              padding: isMobile ? '12px 24px' : '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: isMobile ? '16px' : '14px',
              minHeight: isMobile ? '44px' : 'auto'
            }}
          >
            {mode === 'setup' && step !== 'choice' ? 'Back' : 'Cancel'}
          </button>
        </div>
      </div>
      <ConfirmDialog
        open={!!additionalMethodPrompt}
        title={additionalMethodPrompt?.completedMethod === 'totp' ? '✅ TOTP setup successful!' : '✅ Security Key/Biometric setup successful!'}
        message={additionalMethodPrompt?.completedMethod === 'totp'
          ? 'Would you like to set up an additional security method (Security Key/Biometric) for backup protection?'
          : 'Would you like to set up an additional method (Authenticator App) for backup protection?'}
        confirmLabel="Set Up Backup Method"
        cancelLabel="Not Now"
        onConfirm={() => {
          setStep(additionalMethodPrompt.nextStep);
          setError('');
          setVerificationCode('');
          setAdditionalMethodPrompt(null);
        }}
        onCancel={() => {
          onSuccess(additionalMethodPrompt.completedMethod);
          setAdditionalMethodPrompt(null);
        }}
      />
    </div>
  );
};

export default TwoFactorAuth;